import { create } from 'zustand';
import type { Schedule, ScheduleItem, ScheduleGroup } from '@scio3/shared-types';

export type ScheduleDialogType =
  | 'createSchedule'
  | 'editEvent'
  | 'recurrence'
  | 'defaultContent'
  | 'globalDefault'
  | 'createFolder'
  | 'renameFolder'
  | 'moveFolder'
  | 'deleteFolder'
  | 'renameSchedule'
  | 'moveSchedule'
  | 'deleteSchedule'
  | 'confirmScope'
  | 'pushToScreens'
  | 'contentPicker'
  | 'confirmOverlap'
  | 'changePermission'
  | 'viewPermission'
  | 'moveTeam'
  | 'preview'
  | null;

export interface ScheduleDialogContext {
  schedule?: Schedule;
  scheduleItem?: ScheduleItem;
  folder?: ScheduleGroup;
  /** Schedule IDs for bulk move operations */
  scheduleIds?: string[];
  /** Folder (group) IDs for bulk move operations */
  groupIds?: string[];
  dateRange?: { start: string; end: string };
  isNew?: boolean;
  /** Callback after scope confirmation (for drag-drop / recurring edit) */
  onScopeConfirm?: (scope: 'SINGLE' | 'ALL') => void;
  /** Whether the rrule changed (forces "All" scope — hides "Single" button) */
  rruleChanged?: boolean;
  /** Context mode for confirmScope dialog: 'edit' or 'delete' */
  scopeMode?: 'edit' | 'delete';
  /** Callback on cancel/dismiss of confirmScope dialog (e.g., to revert drag) */
  onScopeCancel?: () => void;
  /** Callback after overlap confirmation */
  onOverlapConfirm?: () => void;
  /** Callback on cancel/dismiss of overlap dialog (e.g., to revert drag) */
  onOverlapCancel?: () => void;
  /** Overlapping event items (full objects for date/time display + delete) */
  overlappingItems?: ScheduleItem[];
  /** The specific occurrence start date when clicking a recurring event on the calendar */
  occurrenceStart?: string;
  /** Return dialog after content picker selection (for default content flows) */
  returnDialog?: ScheduleDialogType;
  /** Selected content info preserved across content picker navigation */
  selectedContent?: { type: 'asset' | 'playlist'; id: string; name: string; assetAppType?: string };
  /** Preserved default type when navigating to content picker */
  defaultType?: number;
  /** Custom RRULE string passed back from RecurrenceDialog (new event flow) */
  customRRule?: string;
  /** Preserved repeat ID when navigating to content picker (prevents selectBox auto-rrule from overriding user's choice) */
  repeatId?: string;
  /** Preserved event color when navigating to content picker */
  color?: string;
  /** Preserved event text color when navigating to content picker */
  textColor?: string;
  /** Preserved scale setting when navigating between dialogs */
  scale?: string;
  /** Preserved duration setting when navigating between dialogs */
  duration?: number;
  /** Original multi-day date range from selectBox drag (before normalization to same-day) */
  originalDateRange?: { start: string; end: string };
  /** Whether the current event was created via multi-day selectBox drag */
  eventSelectBox?: boolean;
  /** Weekday indices (0=Sun, 6=Sat) from the selectBox selection for auto-rrule */
  selectedDays?: number[];
}

interface ScheduleDialogState {
  activeDialog: ScheduleDialogType;
  dialogContext: ScheduleDialogContext;
  openDialog: (dialog: ScheduleDialogType, context?: ScheduleDialogContext) => void;
  closeDialog: () => void;
}

export const useScheduleDialogStore = create<ScheduleDialogState>((set) => ({
  activeDialog: null,
  dialogContext: {},
  openDialog: (activeDialog, dialogContext = {}) => set({ activeDialog, dialogContext }),
  closeDialog: () => set({ activeDialog: null, dialogContext: {} }),
}));
